import * as React from 'react';

import { useApplicationContext } from '../../utils/applicationContext';
import { trackMoreButtonClick, trackOfferItemClick, trackOffersView } from './tracking';
import { EStatus } from 'shared/types/status';
import { fetchOffers } from 'shared/services/fetchOffers';
import { TOffer } from 'shared/types/offer';
import { TProduct } from 'shared/types/product';
import { canParseUrl } from 'shared/utils/canParseUrl/canParseUrl';

type TUseOffersParams = {
  url: string;
  limit: number;
  isEditor: boolean;
  staticRegion: boolean;
  /**
   * Управляет, должны ли офферы загружаться.
   */
  shouldLoad?: boolean;
};

export const useOffers = ({ url, limit, isEditor, staticRegion, shouldLoad = true }: TUseOffersParams) => {
  const context = useApplicationContext();

  const [offers, setOffers] = React.useState<TOffer[]>([]);
  const [loadStatus, setLoadStatus] = React.useState<EStatus>(EStatus.Loading);

  const properUrl = React.useMemo(() => {
    if (isEditor || !canParseUrl(url)) {
      return '';
    }

    const properUrl = new URL(url);
    properUrl.hostname = context.custom.subdomain + properUrl.hostname.substring(properUrl.hostname.indexOf('.'));

    if (properUrl.searchParams.has('region')) {
      properUrl.searchParams.set('region', String(context.custom.userLocationId));
    }

    return properUrl.toString();
  }, [context.custom.subdomain, context.custom.userLocationId, isEditor, url]);

  const urlToUse = staticRegion ? url : properUrl;

  const onShow = React.useCallback(() => {
    const products = offers
      .map(({ id }, index) => ({
        id,
        position: index + 1,
        offerType: 'offer',
      }))
      .filter(({ id }) => id);
    trackOffersView(products as TProduct[]);
  }, [offers]);

  React.useEffect(() => {
    // Ранний выход, если загрузка еще не запрошена
    if (!shouldLoad) {
      return;
    }

    const fetchData = async () => {
      setLoadStatus(EStatus.Loading);

      try {
        const { hostname, pathname, search } = new URL(urlToUse);
        const subdomain = hostname.split('.')[0];

        const offers = await fetchOffers(context, {
          subdomain,
          path: pathname + search,
          limit,
        });

        setOffers(offers.slice(0, limit));
        setLoadStatus(EStatus.Succeed);
      } catch (e) {
        setLoadStatus(EStatus.Failed);
      }
    };

    if (isEditor || !canParseUrl(urlToUse)) {
      return;
    }

    fetchData();
  }, [context, isEditor, limit, urlToUse, shouldLoad]);

  return React.useMemo(
    () => ({
      offers,
      loadStatus,
      onShow,
      urlToUse,
      onOfferClick: trackOfferItemClick,
      onMoreClick: trackMoreButtonClick,
    }),
    [offers, onShow, loadStatus, urlToUse],
  );
};
